import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { publicApi } from '../utils/api';
import { FEATURED_GUIDES_BY_SLUG } from '../data/editorialContent';

const renderGuideBody = (body = '') => body.split('\n').map((line, index) => {
  if (line.startsWith('### ')) return <h3 key={index} className="text-xl font-bold text-white mt-8 mb-3">{line.slice(4)}</h3>;
  if (line.startsWith('## ')) return <h2 key={index} className="text-2xl font-bold text-white mt-10 mb-4">{line.slice(3)}</h2>;
  if (line.startsWith('- ')) return <li key={index} className="ml-6 mb-2 text-sm md:text-base">{line.slice(2)}</li>;
  if (line.trim() === '') return <div key={index} className="h-3" />;
  return <p key={index} className="mb-3">{line}</p>;
});

const GuideDetailPage = () => {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await publicApi.getGuideBySlug(slug);
        setGuide(response.data || FEATURED_GUIDES_BY_SLUG[slug] || null);
      } catch {
        setGuide(FEATURED_GUIDES_BY_SLUG[slug] || null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen pt-24 page-bg-shell page-bg-devices"><section className="container mx-auto px-6 py-16"><div className="terminal-grid p-6"><p style={{ color: 'var(--text-secondary)' }}>Loading guide...</p></div></section></div>;
  }

  if (!guide) {
    return <div className="min-h-screen pt-24 page-bg-shell page-bg-devices"><section className="container mx-auto px-6 py-16"><div className="terminal-grid p-6"><p style={{ color: 'var(--text-secondary)' }}>Guide not found.</p></div></section></div>;
  }

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-devices" data-testid="guide-detail-page">
      <section className="container mx-auto px-6 py-16">
      <div className="max-w-4xl mx-auto">
        <Link to="/guides" className="text-sm" style={{ color: 'var(--accent-primary)' }}>Back to guides</Link>
        <p className="text-xs uppercase font-mono mt-6 mb-3" style={{ color: 'var(--text-muted)' }}>{guide.category || 'guide'}</p>
        <h1 className="text-4xl font-black mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>{guide.title}</h1>
        {guide.excerpt && <p className="text-lg mb-8" style={{ color: 'var(--text-secondary)' }}>{guide.excerpt}</p>}
        {guide.cover_image && (
          <img src={guide.cover_image} alt={guide.title} className="w-full h-64 md:h-80 object-cover rounded-2xl mb-8" />
        )}
        {guide.visual && (
          <div className="grid md:grid-cols-[1.4fr_0.8fr] gap-6 mb-8">
            <div className="terminal-grid p-6">
              <p className="text-xs uppercase font-mono mb-4" style={{ color: 'var(--text-muted)' }}>{guide.visual.title || 'Guide flow'}</p>
              <div className="space-y-4">
                {(guide.visual.steps || []).map((step, index) => (
                  <div key={step.label} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'rgba(255,232,0,0.12)', color: 'var(--accent-primary)' }}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{step.label}</p>
                      <p style={{ color: 'var(--text-secondary)' }}>{step.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="terminal-grid p-6">
              <p className="text-xs uppercase font-mono mb-4" style={{ color: 'var(--text-muted)' }}>Quick signals</p>
              <div className="space-y-4">
                {(guide.visual.stats || []).map((stat) => (
                  <div key={stat.label} className="pb-4" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <p className="text-2xl font-black text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>{stat.value}</p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="terminal-grid p-8 leading-7" style={{ color: 'var(--text-secondary)' }}>
          {guide.body ? renderGuideBody(guide.body) : 'No body content has been published yet.'}
        </div>
        <div className="mt-8 inline-flex">
          <Link to="/consulting#packages" className="btn-secondary inline-flex items-center gap-2">
            Explore packages and pricing <ArrowRight size={14} />
          </Link>
        </div>
      </div>
      </section>
    </div>
  );
};

export default GuideDetailPage;
